import type { CSRTimelinePoint } from '@/types/api';

export interface ChartDateLabel {
  date: string;
  monthDayDisplay: string;
  yearMarker?: string;
  fullDateDisplay: string;
}

export interface ComparePointMeta {
  pointType: 'win' | 'loss' | 'missed';
  played: boolean;
  result?: string | null;
  showDot?: boolean;
  // Match detail fields for popover
  opponent_name?: string | null;
  player_games_won?: number | null;
  opponent_games_won?: number | null;
  player_race?: number | null;
  opponent_race?: number | null;
  venue?: string | null;
  division_name?: string | null;
  is_makeup_match?: boolean | null;
  csr_before?: number | null;
}

export interface YearBackgroundBand {
  year: number;
  startDate: string;
  endDate: string;
}

export interface YearSection {
  year: number;
  label: string;
  startDate: string;
  endDate: string;
  shaded: boolean;
}

export type CompareChartRow = ChartDateLabel & {
  xKey: string;
  sameDayIndex: number;
  isSpacer?: boolean;
  isMatchDay?: boolean;
  isGroupedMatchDay?: boolean;
} & Record<string, string | number | boolean | ComparePointMeta | undefined>;
export interface CompareChartSeries {
  playerId: string;
  timeline?: CSRTimelinePoint[];
  earliestActualDate?: string;
}
export interface BuildCompareChartOptions {
  padMatchDays?: boolean;
  groupSameDayMatches?: boolean;
}
export interface CompareTooltipEntry {
  playerId: string;
  playerName: string;
  value: number;
  color?: string;
  missed: boolean;
}

const MONTH_DAY_COLOR = '#94a3b8';

function isPlayedTimelinePoint(point: CSRTimelinePoint): boolean {
  return Boolean(point.played) || point.point_type === 'win' || point.point_type === 'loss' || Boolean(point.result);
}

export function getTimelineFirstPlayedDate(timeline?: CSRTimelinePoint[]): string | undefined {
  const firstPlayedPoint = (timeline ?? []).find(isPlayedTimelinePoint);
  return firstPlayedPoint?.date.split('T')[0];
}

export function getTimelineWinLoss(timeline?: CSRTimelinePoint[]): { wins: number; losses: number } {
  return (timeline ?? []).reduce(
    (stats, point) => {
      if (!isPlayedTimelinePoint(point)) return stats;
      if (point.result === 'W' || point.point_type === 'win') stats.wins += 1;
      if (point.result === 'L' || point.point_type === 'loss') stats.losses += 1;
      return stats;
    },
    { wins: 0, losses: 0 },
  );
}

export function buildChartDateLabels(dateKeys: string[]): ChartDateLabel[] {
  const uniqueSortedDateKeys = Array.from(new Set(dateKeys)).sort();
  if (!uniqueSortedDateKeys.length) return [];

  const years = new Set(uniqueSortedDateKeys.map((dateKey) => new Date(dateKey + 'T12:00:00').getFullYear()));

  return uniqueSortedDateKeys.map((dateKey, index) => {
    const date = new Date(dateKey + 'T12:00:00');
    const year = date.getFullYear();
    const previousDateKey = index > 0 ? uniqueSortedDateKeys[index - 1] : null;
    const previousYear = previousDateKey ? new Date(previousDateKey + 'T12:00:00').getFullYear() : null;

    return {
      date: dateKey,
      monthDayDisplay: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      yearMarker: years.size > 1 && previousYear !== year ? `'${String(year).slice(-2)}` : undefined,
      fullDateDisplay: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
    };
  });
}

export function buildChartDateLabelMap(dateKeys: string[]): Map<string, ChartDateLabel> {
  return new Map(buildChartDateLabels(dateKeys).map((label) => [label.date, label]));
}

export function buildYearSections(dateKeys: string[]): YearSection[] {
  const labels = buildChartDateLabels(dateKeys);
  if (!labels.length) return [];

  const years = Array.from(
    new Set(labels.map((label) => new Date(label.date + 'T12:00:00').getFullYear()))
  ).sort((a, b) => a - b);

  if (years.length <= 1) return [];

  const latestYear = years[years.length - 1];
  return years.map((year) => {
    const yearLabels = labels.filter((label) => new Date(label.date + 'T12:00:00').getFullYear() === year);
    return {
      year,
      label: String(year),
      startDate: yearLabels[0].date,
      endDate: yearLabels[yearLabels.length - 1].date,
      shaded: (latestYear - year) % 2 === 1,
    };
  });
}

export function buildYearBackgroundBands(dateKeys: string[]): YearBackgroundBand[] {
  return buildYearSections(dateKeys)
    .filter((section) => section.shaded)
    .map(({ year, startDate, endDate }) => ({ year, startDate, endDate }));
}

// Division accent color for same-day clustered dots.
// Uses the division name to infer the type — LC divisions get amber, 8-ball get indigo.
function getDivisionAccentColor(payload: any): string | null {
  if (!payload?.same_day_count || payload.same_day_count <= 1) return null;
  const divName = (payload.division_name ?? '').toLowerCase();
  if (/\blc\b|lagger/.test(divName)) return '#f59e0b'; // amber-500
  return '#6366f1'; // indigo-500
}

// Horizontal pixel offset for same-day clustered dots.
// Spreads N points symmetrically around the original cx.
const CLUSTER_SPREAD_PX = 10;
function getClusterOffset(payload: any): number {
  const count = payload?.same_day_count ?? 1;
  const index = payload?.same_day_index ?? 0;
  if (count <= 1) return 0;
  // Center the cluster: for 2 points → offsets -0.5, +0.5 times spread
  const center = (count - 1) / 2;
  return (index - center) * CLUSTER_SPREAD_PX;
}

export function renderHistoryPointDot(props: any) {
  const { cx, cy, payload, onMouseEnter, onMouseLeave, onClick } = props;
  if (cx == null || cy == null || !payload) return <g />;

  const requestedRadius = typeof props.r === 'number' ? props.r : 0;
  const offsetX = getClusterOffset(payload);

  if (payload.pointType === 'missed') {
    if (payload.showDot === false) return <g />;

    const radius = Math.max(requestedRadius, 4);
    return (
      <circle
        cx={cx + offsetX}
        cy={cy}
        r={radius}
        fill="#ffffff"
        fillOpacity={0.65}
        stroke="#94a3b8"
        strokeOpacity={0.7}
        strokeWidth={1.5}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      />
    );
  }

  const radius = Math.max(requestedRadius, 6.5);
  const fill = payload.pointType === 'win' ? '#10b981' : '#ef4444';
  const divColor = getDivisionAccentColor(payload);
  const dotCx = cx + offsetX;

  // On mobile, touchEnd fires but synthetic click may be swallowed by Recharts.
  // Fire the click handler directly from touchEnd for reliable mobile tap.
  const handleTouchEnd = onClick
    ? (e: React.TouchEvent) => { e.preventDefault(); e.stopPropagation(); onClick(); }
    : undefined;

  return (
    <g style={{ cursor: onClick ? 'pointer' : 'default' }}
       onMouseEnter={onMouseEnter}
       onMouseLeave={onMouseLeave}
       onClick={onClick}
       onTouchEnd={handleTouchEnd}
    >
      {/* Division accent ring — visible only for same-day clustered points */}
      {divColor && (
        <circle
          cx={dotCx} cy={cy}
          r={radius + 3}
          fill="none"
          stroke={divColor}
          strokeWidth={2}
          strokeOpacity={0.8}
        />
      )}
      {/* Main dot */}
      <circle
        cx={dotCx} cy={cy}
        r={radius}
        fill={fill}
        stroke="#ffffff"
        strokeWidth={1.5}
      />
    </g>
  );
}

export function renderHistoryPointActiveDot(props: any) {
  if (props?.payload?.pointType === 'missed') {
    return renderHistoryPointDot({ ...props, r: 5, payload: { ...props.payload, showDot: true } });
  }
  return renderHistoryPointDot({ ...props, r: 6.5 });
}

function markMissedRunEndpoints(rows: CompareChartRow[], playerId: string) {
  let runStart = -1;

  const flushRun = (runEnd: number) => {
    if (runStart === -1) return;

    for (let index = runStart; index <= runEnd; index += 1) {
      const meta = rows[index][`__meta_${playerId}`] as ComparePointMeta | undefined;
      if (!meta || meta.pointType !== 'missed') continue;

      meta.showDot = index === runStart || index === runEnd;
    }

    runStart = -1;
  };

  rows.forEach((row, index) => {
    const meta = row[`__meta_${playerId}`] as ComparePointMeta | undefined;
    if (meta?.pointType === 'missed') {
      if (runStart === -1) runStart = index;
      return;
    }

    flushRun(index - 1);
  });

  flushRun(rows.length - 1);
}

export function makeYearLabelRenderer(yearSections: YearSection[]) {
  return function renderYearLabels(props: any) {
    const xAxis = Object.values(props?.xAxisMap ?? {})[0] as { scale?: (value: string) => number } | undefined;
    const offset = props?.offset as { top?: number; height?: number } | undefined;
    const scale = xAxis?.scale as (((value: string) => number) & { bandwidth?: () => number }) | undefined;
    if (!scale || offset?.top == null || offset?.height == null || yearSections.length <= 1) return null;

    const bandwidth = typeof scale.bandwidth === 'function' ? scale.bandwidth() : 0;
    const labelY = offset.top + offset.height + 60;

    return (
      <g>
        {yearSections.map((section) => {
          const startX = scale(section.startDate);
          const endX = scale(section.endDate);
          if (typeof startX !== 'number' || typeof endX !== 'number') return null;

          const centerX = (startX + endX + bandwidth) / 2;
          return (
            <text
              key={section.year}
              x={centerX}
              y={labelY}
              textAnchor="middle"
              fill="#64748b"
              fontSize={10}
              fontWeight={500}
            >
              {section.label}
            </text>
          );
        })}
      </g>
    );
  };
}

export function makeDateAxisTickRenderer(dateLabelMap: Map<string, ChartDateLabel>) {
  return function renderDateAxisTick(props: any) {
    const { x, y, payload } = props;
    const point = dateLabelMap.get(payload?.value);
    if (x == null || y == null || !point) return <g />;

    return (
      <g transform={`translate(${x},${y})`}>
        <text x={0} y={0} dy={16} textAnchor="middle" fill={MONTH_DAY_COLOR} fontSize={11}>
          {point.monthDayDisplay}
        </text>
      </g>
    );
  };
}

export function buildCompareChartData(
  series: CompareChartSeries[],
  options: BuildCompareChartOptions = {},
): CompareChartRow[] {
  const labels = buildChartDateLabels(
    series.flatMap((entry) => (entry.timeline ?? []).map((point) => point.date.split('T')[0]))
  );
  if (!labels.length) return [];

  const pointCountByDate = new Map<string, number>();
  const matchDates = new Set<string>();
  series.forEach(({ timeline }) => {
    const countByDate = new Map<string, number>();
    (timeline ?? []).forEach((point) => {
      const date = point.date.split('T')[0];
      countByDate.set(date, (countByDate.get(date) ?? 0) + 1);
      if (isPlayedTimelinePoint(point)) {
        matchDates.add(date);
      }
    });
    countByDate.forEach((count, date) => {
      pointCountByDate.set(date, Math.max(pointCountByDate.get(date) ?? 0, count));
    });
  });

  const rows = labels.flatMap((label) => {
    const pointCount = pointCountByDate.get(label.date) ?? 1;
    const isMatchDay = matchDates.has(label.date);
    const shouldGroupMatchDay = (
      (options.padMatchDays && isMatchDay) ||
      (options.groupSameDayMatches !== false && pointCount > 1)
    );
    const matchRows = Array.from({ length: pointCount }, (_, sameDayIndex) => ({
      ...label,
      xKey: `${label.date}#${sameDayIndex}`,
      sameDayIndex,
      isMatchDay,
      isGroupedMatchDay: shouldGroupMatchDay,
    }));
    if (!shouldGroupMatchDay) return matchRows;

    // Empty categories keep match dots inside, rather than on, their date band's boundaries.
    return [
      { ...label, xKey: `${label.date}#before`, sameDayIndex: -1, isSpacer: true, isMatchDay, isGroupedMatchDay: true },
      ...matchRows,
      { ...label, xKey: `${label.date}#after`, sameDayIndex: pointCount, isSpacer: true, isMatchDay, isGroupedMatchDay: true },
    ];
  }) as CompareChartRow[];

  series.forEach(({ playerId, timeline, earliestActualDate }) => {
    const actualByDate = new Map<string, CSRTimelinePoint[]>();
    (timeline ?? []).forEach((point) => {
      const date = point.date.split('T')[0];
      actualByDate.set(date, [...(actualByDate.get(date) ?? []), point]);
    });
    const visibleStartDate = earliestActualDate ?? getTimelineFirstPlayedDate(timeline);
    let lastKnownPoint: CSRTimelinePoint | undefined;

    rows.forEach((row) => {
      if (row.isSpacer) return;
      const actualPoint = actualByDate.get(row.date)?.[row.sameDayIndex];
      if (actualPoint) {
        if (
          visibleStartDate &&
          row.date < visibleStartDate &&
          !isPlayedTimelinePoint(actualPoint)
        ) {
          return;
        }

        row[playerId] = Math.round(actualPoint.csr);
        row[`__meta_${playerId}`] = {
          pointType: actualPoint.point_type ?? 'missed',
          played: Boolean(actualPoint.played),
          result: actualPoint.result,
          opponent_name: actualPoint.opponent_name,
          player_games_won: actualPoint.player_games_won,
          opponent_games_won: actualPoint.opponent_games_won,
          player_race: actualPoint.player_race,
          opponent_race: actualPoint.opponent_race,
          venue: actualPoint.venue,
          division_name: actualPoint.division_name,
          is_makeup_match: actualPoint.is_makeup_match,
          csr_before: actualPoint.csr_before,
        };
        lastKnownPoint = actualPoint;
        return;
      }

      if (!lastKnownPoint) return;
      if (visibleStartDate && row.date < visibleStartDate) return;

      row[playerId] = Math.round(lastKnownPoint.csr);
      row[`__meta_${playerId}`] = {
        pointType: 'missed',
        played: false,
        result: null,
      };
    });

    markMissedRunEndpoints(rows, playerId);
  });

  return rows;
}

export function getComparePointMeta(payload: CompareChartRow, playerId: string): ComparePointMeta | undefined {
  return payload[`__meta_${playerId}`] as ComparePointMeta | undefined;
}

export function getCompareTooltipEntries(
  payload: any[] | undefined,
  getPlayerName: (playerId: string) => string
): CompareTooltipEntry[] {
  if (!Array.isArray(payload)) return [];

  return payload.flatMap((entry) => {
    const playerId = String(entry?.name ?? entry?.dataKey ?? '');
    const numericValue = Number(entry?.value);
    if (!playerId || !Number.isFinite(numericValue)) return [];

    const row = entry?.payload as CompareChartRow | undefined;
    const meta = row ? getComparePointMeta(row, playerId) : undefined;
    if (!meta) return [];

    return [{
      playerId,
      playerName: getPlayerName(playerId) || playerId,
      value: Math.round(numericValue),
      color: typeof entry?.color === 'string' ? entry.color : undefined,
      missed: meta.pointType === 'missed' && !meta.played,
    }];
  });
}

export function getVisibleCompareTooltipEntries(
  entries: CompareTooltipEntry[],
  focusedPlayerId?: string | null
): CompareTooltipEntry[] {
  if (!focusedPlayerId) return entries;

  const focusedEntries = entries.filter((entry) => entry.playerId === focusedPlayerId);
  return focusedEntries.length ? focusedEntries : entries;
}

export function makeCompareTooltipContent(
  fullDateDisplayByDate: Map<string, string>,
  getPlayerName: (playerId: string) => string,
  focusedPlayerId?: string | null
) {
  return function renderCompareTooltipContent(props: any) {
    const { active, label, payload } = props;
    const entries = getVisibleCompareTooltipEntries(
      getCompareTooltipEntries(payload, getPlayerName),
      focusedPlayerId
    );
    if (!active || !entries.length) return null;

    return (
      <div className="rounded-[10px] border border-slate-200 bg-white px-4 py-3 text-xs shadow-[0_4px_12px_rgba(0,0,0,0.08)]">
        <div className="mb-2 text-slate-700">
          {fullDateDisplayByDate.get(label) ?? label}
        </div>
        <div className="space-y-1.5">
          {entries.map((entry) => (
            <div key={entry.playerId} className="flex items-center justify-between gap-4">
              <span
                className={entry.missed ? 'decoration-slate-300 line-through opacity-60' : undefined}
                style={{ color: entry.color ?? '#334155' }}
              >
                {entry.playerName}
              </span>
              <span className="font-semibold text-slate-700">{entry.value}</span>
            </div>
          ))}
        </div>
      </div>
    );
  };
}

// ---------------------------------------------------------------------------
// Rich match-detail tooltip (for PlayerHistoryChart & ComparePlayersModal)
// Shows full match info on hover — replaces the simple CSR-only tooltip.
// ---------------------------------------------------------------------------

interface RichTooltipMeta {
  pointType?: 'win' | 'loss' | 'missed';
  result?: string | null;
  opponent_name?: string | null;
  player_games_won?: number | null;
  opponent_games_won?: number | null;
  player_race?: number | null;
  opponent_race?: number | null;
  venue?: string | null;
  division_name?: string | null;
  is_makeup_match?: boolean | null;
  csr_before?: number | null;
}

function formatRichTooltipDate(iso: string): string {
  const d = new Date(iso + (iso.includes('T') ? '' : 'T12:00:00'));
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function RichMatchTooltipInner({
  date,
  csr,
  meta,
  playerName,
  playerColor,
  onHeadToHead,
}: {
  date: string;
  csr: number;
  meta: RichTooltipMeta;
  playerName?: string;
  playerColor?: string;
  onHeadToHead?: (opponentName: string) => void;
}) {
  const isWin = meta.result === 'W' || meta.pointType === 'win';
  const hasScore = meta.player_games_won != null && meta.opponent_games_won != null;
  const hasRace = meta.player_race != null && meta.opponent_race != null;

  return (
    <div
      className="rounded-[10px] border border-slate-200 bg-white px-3.5 py-2.5 text-xs shadow-[0_4px_12px_rgba(0,0,0,0.08)]"
      style={{ minWidth: 180, lineHeight: 1.6 }}
    >
      {/* Header */}
      <div className="flex items-center gap-2 mb-1">
        {playerName && (
          <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: playerColor ?? '#6366f1' }} />
        )}
        <span className="font-semibold text-slate-700">
          {playerName ?? formatRichTooltipDate(date)}
        </span>
      </div>
      {playerName && <div className="text-slate-500 mb-1">{formatRichTooltipDate(date)}</div>}

      {/* Result + Opponent */}
      {meta.pointType !== 'missed' && (
        <div className="flex items-center gap-2 mb-1">
          <span
            className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold ${
              isWin ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-600'
            }`}
          >
            {isWin ? 'WIN' : 'LOSS'}
          </span>
          {meta.opponent_name ? (
            <span className="text-slate-700 font-medium truncate">vs. {meta.opponent_name}</span>
          ) : (
            <span className="text-slate-400 italic">Opponent unknown</span>
          )}
        </div>
      )}

      {meta.pointType === 'missed' && (
        <div className="text-slate-400 italic mb-1">No match played</div>
      )}

      {/* Details */}
      <div className="space-y-0.5 pt-1 border-t border-slate-100">
        {hasScore && (
          <div className="flex justify-between">
            <span className="text-slate-500">Score</span>
            <span className="font-semibold text-slate-700">{meta.player_games_won} – {meta.opponent_games_won}</span>
          </div>
        )}
        {hasRace && (
          <div className="flex justify-between">
            <span className="text-slate-500">Race</span>
            <span className="font-semibold text-slate-700">{meta.player_race} to {meta.opponent_race}</span>
          </div>
        )}
        <div className="flex justify-between">
          <span className="text-slate-500">CSR</span>
          <span className="font-semibold text-slate-700 flex items-center gap-1.5">
            {meta.csr_before != null && (
              <>
                <span className="text-slate-400 font-normal">{meta.csr_before}</span>
                <span className="text-slate-300">→</span>
              </>
            )}
            {csr}
            {meta.csr_before != null && (
              <span className={`text-[10px] font-medium ${
                csr > meta.csr_before ? 'text-emerald-600' : csr < meta.csr_before ? 'text-red-500' : 'text-slate-400'
              }`}>
                {csr > meta.csr_before ? '+' : ''}{csr - meta.csr_before}
              </span>
            )}
          </span>
        </div>
        {meta.division_name && (
          <div className="flex justify-between gap-2">
            <span className="text-slate-500 flex-shrink-0">Division</span>
            <span className="text-slate-600 text-right leading-tight">{meta.division_name}</span>
          </div>
        )}
        {meta.venue && (
          <div className="flex justify-between gap-2">
            <span className="text-slate-500 flex-shrink-0">Venue</span>
            <span className="text-slate-600 text-right leading-tight">{meta.venue}</span>
          </div>
        )}
      </div>

      {meta.is_makeup_match && (
        <div className="pt-1 mt-1 border-t border-slate-100">
          <span className="inline-flex items-center rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-medium text-amber-700 border border-amber-200">
            Makeup match
          </span>
        </div>
      )}

      {onHeadToHead && meta.opponent_name && meta.pointType !== 'missed' && (
        <div className="pt-1.5 mt-1.5 border-t border-slate-100">
          <button
            onClick={(e) => { e.stopPropagation(); onHeadToHead(meta.opponent_name!); }}
            onTouchEnd={(e) => { e.preventDefault(); e.stopPropagation(); onHeadToHead(meta.opponent_name!); }}
            className="w-full flex items-center justify-center gap-1.5 px-2 py-1.5 text-xs font-medium text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 active:bg-indigo-100 rounded-lg transition-colors"
          >
            <span>⚔️</span>
            Head to Head
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * Rich tooltip for single-player history chart (PlayerHistoryChart).
 * Shows full match details on hover.
 */
export function makeSinglePlayerRichTooltip(selectedMatch: unknown) {
  return function renderSinglePlayerRichTooltip(props: any) {
    if (selectedMatch) return null;

    const { active, payload } = props;
    if (!active || !payload?.length) return null;

    const point = payload[0]?.payload;
    if (!point) return null;

    const csr = typeof point.csr === 'number' ? Math.round(point.csr) : 0;

    return (
      <RichMatchTooltipInner
        date={point.date ?? point.fullDateDisplay ?? ''}
        csr={csr}
        meta={{
          pointType: point.pointType,
          result: point.result,
          opponent_name: point.opponent_name,
          player_games_won: point.player_games_won,
          opponent_games_won: point.opponent_games_won,
          player_race: point.player_race,
          opponent_race: point.opponent_race,
          venue: point.venue,
          division_name: point.division_name,
          is_makeup_match: point.is_makeup_match,
          csr_before: point.csr_before,
        }}
      />
    );
  };
}

/**
 * Rich tooltip for compare-players chart (ComparePlayersModal).
 * Shows full match details for the focused player on hover.
 */
export function makeCompareRichTooltip(
  fullDateDisplayByDate: Map<string, string>,
  getPlayerName: (playerId: string) => string,
  getPlayerColor: (playerId: string) => string,
  focusedPlayerId: string | null,
  selectedMatch: unknown,
  onHeadToHead?: (playerId: string, opponentName: string) => void,
) {
  return function renderCompareRichTooltip(props: any) {
    if (selectedMatch) return null;

    const { active, label, payload } = props;
    if (!active || !payload?.length) return null;

    // If a player is focused (hovering over their dot), show their rich detail
    if (focusedPlayerId) {
      const entry = payload.find((e: any) => (e?.name ?? e?.dataKey) === focusedPlayerId);
      if (!entry) return null;

      const row = entry.payload as CompareChartRow | undefined;
      const meta = row ? getComparePointMeta(row, focusedPlayerId) : undefined;
      if (!meta || !Number.isFinite(Number(entry.value))) return null;

      const csr = Math.round(Number(entry.value));
      const pid = focusedPlayerId;
      return (
        <RichMatchTooltipInner
          date={row?.date ?? label}
          csr={csr}
          meta={meta}
          playerName={getPlayerName(focusedPlayerId)}
          playerColor={getPlayerColor(focusedPlayerId)}
          onHeadToHead={onHeadToHead ? (opp) => onHeadToHead(pid, opp) : undefined}
        />
      );
    }

    // No focused player — show simple multi-player CSR list
    const entries = getVisibleCompareTooltipEntries(
      getCompareTooltipEntries(payload, getPlayerName),
      null
    );
    if (!entries.length) return null;

    return (
      <div className="rounded-[10px] border border-slate-200 bg-white px-4 py-3 text-xs shadow-[0_4px_12px_rgba(0,0,0,0.08)]">
        <div className="mb-2 text-slate-700">
          {fullDateDisplayByDate.get(label) ?? label}
        </div>
        <div className="space-y-1.5">
          {entries.map((entry) => (
            <div key={entry.playerId} className="flex items-center justify-between gap-4">
              <span
                className={entry.missed ? 'decoration-slate-300 line-through opacity-60' : undefined}
                style={{ color: entry.color ?? '#334155' }}
              >
                {entry.playerName}
              </span>
              <span className="font-semibold text-slate-700">{entry.value}</span>
            </div>
          ))}
        </div>
      </div>
    );
  };
}
